import { uniqBy } from "lodash-es";
import { type FC, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Icons,
  Item,
  MenuOptionItem,
  MenuOptionItemIntent,
  type MenuOptionItemProps,
  MenuTriggerButton,
  Section,
  TriggerButtonSize,
} from "widgets";
import { type FileNameInfo, getFilenameInfo } from "widgets/utils";

import { type ProjectFile } from "@/graphql.ts";
import { StackTile } from "@/modules/core/components/StackTiles";
import type { TileProps } from "@/modules/core/components/Tile";
import { FileTile } from "@/modules/files/components/FileTile";
import { getFilesApplicableToGenomeViewer } from "@/modules/files/utils/utils.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { getProjectFilesDataForSendingToGenomeViewer } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { EVENT_RESOURCE_TYPES } from "@/settings/settings.ts";
import { FileIcon } from "@/ui-modules/core/FileIcon.tsx";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.tsx";
import { checkAndDownloadProjectFiles } from "@selector/shared/jsx";
import { selMessage } from "@selector/shared";

interface FilesStackTileProps {
  files: ProjectFile[];
  type?: TileProps["type"];
}

interface FilesStackTileMenuProps {
  filesApplicableToGenomeViewer: ProjectFile[];
  filteredFiles: ProjectFile[];
  onOpenGenomeViewer: (files: ProjectFile[] | null) => void;
}

const FilesStackTileMenu: FC<FilesStackTileMenuProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const items: { id: string; items: MenuOptionItemProps[] }[] = [
    { id: "more", items: [{ id: "download-all", title: "Download All", icon: "download", intent: undefined }] },
  ];

  if (props.filesApplicableToGenomeViewer.length) {
    items.unshift({
      id: "send-to",
      items: [
        {
          id: "open-all-genome-viewer",
          title: "Send All to Genome Viewer",
          icon: <img src={Icons.Action.GenomeViewer} width={20} height={20} />,
          intent: MenuOptionItemIntent.Primary,
        },
      ],
    });
  }

  return (
    <MenuTriggerButton
      icon={"more_vert"}
      size={TriggerButtonSize.Small}
      label={""}
      onAction={(key) => {
        switch (String(key)) {
          case "open-all-genome-viewer":
            props.onOpenGenomeViewer(
              dispatch(
                getProjectFilesDataForSendingToGenomeViewer(props.filesApplicableToGenomeViewer.map((file) => file.id)),
              ),
            );
            break;
          case "download-all":
            void checkAndDownloadProjectFiles(
              props.filteredFiles.filter((file) => !file?.hidden && !file?.deleted),
            ).then((unavailableFiles) => {
              if (unavailableFiles.length) {
                const allNames = unavailableFiles.flatMap((f) => (f.fileName != null ? [f.fileName] : []));
                const listed = allNames.slice(0, 5);
                if (allNames.length > 5) {
                  listed.push("...");
                }
                notify.warning(
                  {
                    title: selMessage({
                      id: "file.download.failed.title",
                      values: { filesCount: unavailableFiles.length },
                    }),
                    children: selMessage({
                      id: "file.download.failed.children",
                      values: { fileNames: listed.join("', '"), filesCount: unavailableFiles.length },
                    }),
                  },
                  { autoClose: 5000 },
                );
              }
            });
            break;
        }
      }}
      items={items}
    >
      {(section) => (
        <Section key={section.id} items={section.items}>
          {(item) => (
            <Item key={item.id}>
              <MenuOptionItem {...item} />
            </Item>
          )}
        </Section>
      )}
    </MenuTriggerButton>
  );
};

export const FilesStackTile: FC<FilesStackTileProps> = (props) => {
  const filteredFiles = props.files.filter((file) => file != null);

  const fileInfos = uniqBy<FileNameInfo | null>(
    filteredFiles.map((file) => getFilenameInfo(file?.fileName)).filter((fileInfo) => fileInfo?.extension),
    (fileInfo) => fileInfo?.extension,
  );
  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);
  const filesApplicableToGenomeViewer = getFilesApplicableToGenomeViewer(filteredFiles);

  return (
    <>
      <StackTile
        title={`${filteredFiles.length} ${fileInfos
          .map((fileInfo) => fileInfo?.extension.toUpperCase())
          .join(", ")} files`}
        type={
          props.type === EVENT_RESOURCE_TYPES.UNKNOWN
            ? fileInfos.map((fileInfo) => fileInfo?.typeLabel).join(", ")
            : props.type
        }
        icon={
          <FileIcon
            asStack
            filename={filteredFiles.some((file) => !file.hidden) ? filteredFiles[0]?.fileName : undefined}
          />
        }
        menuButton={
          // do not allow download all when there is at least one restricted file
          filteredFiles.some((file) => !file?.hidden && !file?.deleted) ? (
            <FilesStackTileMenu
              filesApplicableToGenomeViewer={filesApplicableToGenomeViewer}
              filteredFiles={filteredFiles}
              onOpenGenomeViewer={setProjectFilesForGenomeViewer}
            />
          ) : undefined
        }
      >
        {filteredFiles.map((file) => (
          <FileTile file={file} type={props.type} key={file.id} />
        ))}
      </StackTile>

      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onClose={() => {
            setProjectFilesForGenomeViewer(null);
          }}
        />
      )}
    </>
  );
};
